import type { PlayerSkin, TrailId } from '@cube-rush/shared';
import { useEffect, useRef } from 'preact/hooks';
import { cubeAccent, drawCubeIcon, skinColor } from '../../render/icons';
import { prepareCanvas } from '../canvas';
import { drawTrail, type TrailPoint } from './trailArt';

export interface TrailSampleProps {
  skin: PlayerSkin;
  trail: TrailId;
  width?: number;
  height?: number;
}

/** A still picture of a trail behind a small cube, for the trail picker. */
export function TrailSample({ skin, trail, width = 96, height = 44 }: TrailSampleProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { icon, primary, secondary } = skin;

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas && prepareCanvas(canvas, width, height);
    if (!ctx) return;
    const cube = height * 0.42;
    const endX = width - cube * 0.75;
    const points: TrailPoint[] = [];
    // A gentle jump arc ending at the cube.
    for (let i = 0, n = 24; i <= n; i++) {
      const t = i / n;
      const x = t * endX;
      const y = height * 0.62 - Math.sin(t * Math.PI) * height * 0.22;
      points.push({ x, y, id: i + 1 });
    }
    const colors = { primary: cubeAccent({ primary, secondary }), secondary: skinColor(secondary) };
    drawTrail(ctx, trail, points, colors, cube, 0.4);
    const last = points[points.length - 1]!;
    drawCubeIcon(ctx, { icon, primary, secondary }, last.x, last.y, cube);
  }, [icon, primary, secondary, trail, width, height]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{ width: `${width}px`, height: `${height}px` }}
    />
  );
}
